import {
  isGenetecDirectSite,
  isGenetecSite,
  VIDEO_SOURCE_COLUMN_TITLE,
} from "./genetecVideoSource";
import {
  showCameraCredentialColumn,
  showStreamParametersColumn,
} from "./streamPathIntegrations";

/**
 * The connection columns of Edit Camera's General Info table.
 *
 * The header titles (persisted in `localStorage.connectionsTitlesArray` by
 * the load and save paths) and the cells rendered by
 * `GeneralInfoConnectionTableRows` are matched by position, so both must be
 * derived from the same predicates. This module is that single source of
 * truth: `getConnectionColumnKeys` decides which columns exist and in which
 * order, `buildConnectionsTitlesArray` turns that into titles, and the row
 * renderers use the same `show*Column` predicates for their cells.
 */
export type ConnectionColumnKey =
  | "video_source"
  | "host"
  | "rtsp"
  | "http"
  | "server_port"
  | "username"
  | "password"
  | "channel"
  | "stream_parameters"
  | "slice"
  | "split"
  | "stream_quality"
  | "stream_source"
  | "livestream_url";

export const CONNECTION_COLUMN_TITLES: Record<ConnectionColumnKey, string> = {
  video_source: VIDEO_SOURCE_COLUMN_TITLE,
  host: "Host",
  rtsp: "RTSP",
  http: "HTTP Port",
  server_port: "Server Port",
  username: "Username",
  password: "Password",
  channel: "Channel",
  stream_parameters: "Stream Parameters",
  slice: "Slice",
  split: "Split",
  stream_quality: "Stream Quality",
  stream_source: "Stream Source",
  livestream_url: "Live Stream URL",
};

type CameraLike = Record<string, any> | null | undefined;
type SiteLike = Record<string, any> | null | undefined;

/** Read-only effective Video source, Genetec sites only. */
export const showVideoSourceColumn = (siteInfo: SiteLike): boolean =>
  isGenetecSite(siteInfo);

/**
 * Host: shown when the camera has one, and always on a Genetec direct site
 * (the connector dials the unit, so an empty host must be fillable).
 */
export const showHostColumn = (
  cameraObject: CameraLike,
  siteInfo: SiteLike
): boolean => Boolean(cameraObject?.ip) || isGenetecDirectSite(siteInfo);

/**
 * RTSP port: the `rtsp` integration's own column, plus Genetec direct sites.
 */
export const showRtspPortColumn = (siteInfo: SiteLike): boolean =>
  siteInfo?.integration_type_name === "rtsp" || isGenetecDirectSite(siteInfo);

export const getConnectionColumnKeys = (
  cameraObject: CameraLike,
  siteInfo: SiteLike
): ConnectionColumnKey[] => {
  const keys: ConnectionColumnKey[] = [];
  if (showVideoSourceColumn(siteInfo)) {
    keys.push("video_source");
  }
  if (showHostColumn(cameraObject, siteInfo)) {
    keys.push("host");
  }
  if (showRtspPortColumn(siteInfo)) {
    keys.push("rtsp");
  }
  if (cameraObject?.http) {
    keys.push("http");
  }
  if (cameraObject?.server_port) {
    keys.push("server_port");
  }
  if (showCameraCredentialColumn("username", cameraObject, siteInfo)) {
    keys.push("username");
  }
  if (showCameraCredentialColumn("password", cameraObject, siteInfo)) {
    keys.push("password");
  }
  if (cameraObject?.channel) {
    keys.push("channel");
  }
  if (showStreamParametersColumn(cameraObject, siteInfo)) {
    keys.push("stream_parameters");
  }
  if (cameraObject?.slice) {
    keys.push("slice");
  }
  if (cameraObject?.split) {
    keys.push("split");
  }
  if (cameraObject?.use_stream_quality) {
    keys.push("stream_quality");
  }
  if (cameraObject?.stream_source) {
    keys.push("stream_source");
    keys.push("livestream_url");
  }
  return keys;
};

export const buildConnectionsTitlesArray = (
  cameraObject: CameraLike,
  siteInfo: SiteLike
): string[] =>
  getConnectionColumnKeys(cameraObject, siteInfo).map(
    (key) => CONNECTION_COLUMN_TITLES[key]
  );
